import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { AlertTriangle, Clock, CheckCircle2, AlertCircle } from 'lucide-react';

const GrievanceRedressal: React.FC = () => {
  const [grievances, setGrievances] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ category: 'Case Delay', description: '' });

  const fetchGrievances = async () => {
    try {
      const response = await api.get('/grievances/my');
      setGrievances(response.data);
    } catch (error) {
      console.error('Error fetching grievances:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrievances();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/grievances', formData);
      setShowForm(false);
      setFormData({ category: 'Case Delay', description: '' });
      fetchGrievances();
    } catch (error) {
      console.error('Error submitting grievance:', error);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', color: '#1a365d' }}>Grievance Redressal</h3>
        <button 
          onClick={() => setShowForm(!showForm)}
          style={{ padding: '0.5rem 1rem', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          {showForm ? 'Cancel' : 'Submit New Grievance'}
        </button>
      </div>

      {showForm && (
        <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>Submit New Grievance</h4>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Category</label>
              <select 
                value={formData.category} 
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0' }}
              >
                <option value="Case Delay">Case Delay</option>
                <option value="Communication Issue">Communication Issue</option>
                <option value="Fee Dispute">Fee Dispute</option>
                <option value="Staff Behavior">Staff Behavior</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Description</label>
              <textarea 
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                placeholder="Describe your grievance in detail..."
              />
            </div>
            <button type="submit" style={{ width: '100%', padding: '0.75rem', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
              Submit Grievance
            </button>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {grievances.map((g) => (
          <div key={g.id} style={{ padding: '1.5rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 'bold', color: '#1a365d' }}>{g.grievanceId}</span>
                <span style={{ padding: '0.2rem 0.6rem', fontSize: '0.7rem', borderRadius: '9999px', backgroundColor: getStatusColor(g.status).bg, color: getStatusColor(g.status).text, fontWeight: 'bold', textTransform: 'uppercase' }}>
                  {g.status}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#718096' }}>SLA Deadline: {new Date(g.slaDeadline).toLocaleDateString()}</span>
              </div>
              <h4 style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>{g.category}</h4>
              <p style={{ color: '#4a5568', fontSize: '0.875rem' }}>{g.description}</p>
              {g.resolution && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0fff4', borderRadius: '4px', borderLeft: '4px solid #38a169' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#276749' }}>Resolution:</p>
                  <p style={{ fontSize: '0.875rem', color: '#2f855a' }}>{g.resolution}</p>
                </div>
              )}
            </div>
            
            <div style={{ paddingLeft: '1.5rem' }}>
              {getStatusIcon(g.status)}
            </div>
          </div>
        ))}
        {grievances.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '2px dashed #e2e8f0' }}>
            <p style={{ color: '#718096' }}>No grievances submitted. We aim for 100% satisfaction, but we are here to listen if you have concerns.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'registered': return { bg: '#ebf8ff', text: '#2b6cb0' };
    case 'investigating': return { bg: '#fffaf0', text: '#9c4221' };
    case 'resolved': return { bg: '#f0fff4', text: '#276749' };
    case 'closed': return { bg: '#edf2f7', text: '#4a5568' };
    case 'escalated': return { bg: '#fff5f5', text: '#c53030' };
    default: return { bg: '#edf2f7', text: '#4a5568' };
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'registered': return <Clock size={24} color="#2b6cb0" />;
    case 'investigating': return <AlertTriangle size={24} color="#d69e2e" />;
    case 'resolved': return <CheckCircle2 size={24} color="#38a169" />;
    case 'escalated': return <AlertCircle size={24} color="#e53e3e" />;
    default: return null;
  }
};

export default GrievanceRedressal;
